// Antimeridian unwrapping + polygon ring merging for world map geometries.
// Pure functions — no external dependencies except computeBbox.

import { computeBbox } from '@/lib/worldMap/mapUtils';

// Collect all longitudes from a geometry's coordinates tree.
function collectLons(g) {
  const out = [];
  const walk = (c) => {
    if (typeof c[0] === 'number') {
      out.push(c[0]);
      return;
    }
    for (const x of c) walk(x);
  };
  walk(g.coordinates);
  return out;
}

// Two rings share an opposite-direction boundary (the edge between adjacent
// polygon parts, e.g. the dateline cut between Russia's mainland and
// Chukotka). Merge them into a single ring, removing the ENTIRE shared
// boundary — not just one edge — so no internal "spike" remains at the
// junction. Uses a hash map for O(n+m) vertex lookup instead of O(n*m).
function mergeRings(A, B) {
  const same = (a, b) => Math.abs(a[0] - b[0]) < 1e-9 && Math.abs(a[1] - b[1]) < 1e-9;
  // Build a map from B's vertices (rounded) to their indices.
  const bMap = new Map();
  for (let j = 0; j < B.length; j++) {
    const key = `${B[j][0].toFixed(6)},${B[j][1].toFixed(6)}`;
    if (!bMap.has(key)) bMap.set(key, []);
    bMap.get(key).push(j);
  }
  // For each vertex in A, find the matching index in B (if any).
  const aToB = new Array(A.length).fill(-1);
  for (let i = 0; i < A.length; i++) {
    const key = `${A[i][0].toFixed(6)},${A[i][1].toFixed(6)}`;
    const js = bMap.get(key);
    if (js) {
      for (const j of js) {
        if (same(A[i], B[j])) { aToB[i] = j; break; }
      }
    }
  }
  // Find the shared boundary: consecutive vertices in A whose matching
  // vertices in B form a reversed edge (B goes opposite direction to A).
  let iStart = -1, iEnd = -1;
  for (let i = 0; i < A.length; i++) {
    const i1 = (i + 1) % A.length;
    if (aToB[i] >= 0 && aToB[i1] >= 0) {
      // Reversed edge: B[aToB[i1]] -> B[aToB[i]] is the reverse of A[i] -> A[i+1]
      if (aToB[i] === (aToB[i1] + 1) % B.length) {
        if (iStart < 0) iStart = i;
        iEnd = i1;
      }
    }
  }
  if (iStart < 0) return null;
  const jStart = aToB[iStart];
  const jEnd = aToB[iEnd];
  // A path: from iEnd to iStart (forward, wrapping), not crossing the shared boundary.
  const aPath = [];
  for (let k = iEnd; k !== iStart; k = (k + 1) % A.length) aPath.push(A[k]);
  aPath.push(A[iStart]);
  // B path: from (jStart+1) to jEnd (forward, wrapping), not crossing the shared boundary.
  const bPath = [];
  for (let k = (jStart + 1) % B.length; k !== jEnd; k = (k + 1) % B.length) bPath.push(B[k]);
  bPath.push(B[jEnd]);
  let merged = aPath.concat(bPath);
  if (!same(merged[0], merged[merged.length - 1])) merged.push(merged[0]);
  return merged;
}

// Repeatedly merge polygon parts that share an edge — heals the straight
// dateline cut left when antimeridian-spanning countries are unwrapped.
function mergeAdjacentPolys(polys) {
  const result = polys.map((p) => p);
  let changed = true;
  while (changed) {
    changed = false;
    outer: for (let i = 0; i < result.length; i++) {
      for (let j = i + 1; j < result.length; j++) {
        const merged = mergeRings(result[i][0], result[j][0]);
        if (merged) {
          const holes = [...result[i].slice(1), ...result[j].slice(1)];
          result[i] = [merged, ...holes];
          result.splice(j, 1);
          changed = true;
          break outer;
        }
      }
    }
  }
  return result;
}

// For countries crossing the antimeridian (±180°), the part stored with
// negative longitudes (e.g. Russia's Chukotka at -180..-170) renders on the
// far-left edge, splitting the country across both sides of a single-world
// map. Shift those negative-side rings east by +360° so the country shows as
// one continuous shape on the right edge instead, then merge the parts that
// met at the dateline so no internal border line remains.
export function unwrapAntimeridian(features) {
  return features.map((f) => {
    const g = f.geometry;
    if (!g) return f;
    const lons = collectLons(g);
    if (!lons.some((x) => x > 150) || !lons.some((x) => x < -150)) return f;
    const shiftRing = (ring) => {
      if (!ring.every(([lng]) => lng < -150)) return ring;
      return ring.map(([lng, lat]) => [lng < 0 ? lng + 360 : lng, lat]);
    };
    let ng;
    if (g.type === 'Polygon') {
      ng = { ...g, coordinates: g.coordinates.map(shiftRing) };
    } else if (g.type === 'MultiPolygon') {
      const polys = g.coordinates.map((poly) => poly.map(shiftRing));
      ng = { ...g, coordinates: mergeAdjacentPolys(polys) };
    } else {
      ng = g;
    }
    return { ...f, geometry: ng };
  });
}

// Shift admin-1 regions that sit west of -150° east by +360° so they render
// on the right side of the map, aligned with the base country layer (which
// shifts Russia's Chukotka to 180°-190°). A region is only shifted if its
// shifted bbox overlaps a base country feature — so US islands (Aleutians,
// Little Diomede) that are also west of -150° stay put, since the US base
// feature doesn't span the antimeridian.
export function unwrapRegionsAntimeridian(features, baseFeatures) {
  const baseBoxes = [];
  for (const f of baseFeatures) {
    if (!f.geometry) continue;
    baseBoxes.push(computeBbox(f.geometry.coordinates));
  }
  const shiftRing = (ring) => {
    if (!ring.every(([lng]) => lng < -150)) return ring;
    return ring.map(([lng, lat]) => [lng < 0 ? lng + 360 : lng, lat]);
  };
  return features.map((f) => {
    const g = f.geometry;
    if (!g || !f.properties) return f;
    const { minLat, maxLat, minLng, maxLng } = computeBbox(g.coordinates);
    if (minLng >= -150) return f; // not entirely west of -150
    const shiftedMinLng = minLng + 360;
    const shiftedMaxLng = maxLng + 360;
    const overlaps = baseBoxes.some(
      (b) =>
        minLat < b.maxLat &&
        maxLat > b.minLat &&
        shiftedMinLng < b.maxLng &&
        shiftedMaxLng > b.minLng
    );
    if (!overlaps) return f;
    let ng;
    if (g.type === 'Polygon') {
      ng = { ...g, coordinates: g.coordinates.map(shiftRing) };
    } else if (g.type === 'MultiPolygon') {
      ng = { ...g, coordinates: g.coordinates.map((p) => p.map(shiftRing)) };
    } else {
      ng = g;
    }
    return { ...f, geometry: ng };
  });
}